import { CommonModule } from '@angular/common';
import { Component, computed, input } from '@angular/core';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';

import { PrismStatusIndicatorComponent, PrismTooltipComponent } from '@shure/prism-angular-components';
import { DeviceStatus } from '@shure/shared/angular/data-access/system-api/models';
import { provideInlineScope } from '@shure/shared/angular/utils/i18n';

/**
 * Displays a color-coded status indicator bar for device states.
 *
 * This standalone component renders a visual status indicator with tooltips,
 * mapping device status values to Prism color states (good, bad, inactive, ok).
 * Supports localized tooltip messages for each status.
 *
 * @example
 * ```html
 * <sh-device-state-bar [deviceStatus]="deviceStatus()" />
 * ```
 */
@Component({
	selector: 'sh-device-state-bar',
	templateUrl: './device-state-bar.component.html',
	styleUrls: ['./device-state-bar.component.scss'],
	imports: [CommonModule, PrismTooltipComponent, PrismStatusIndicatorComponent, TranslocoModule],
	providers: [provideInlineScope('scDeviceStateBar', (lang) => import(`../i18n/${lang}`))]
})
export class DeviceStateBarComponent {
	/**
	 * Required input signal for the device status to display.
	 */
	public readonly deviceStatus = input.required<DeviceStatus>();

	/**
	 * Computed signal providing the localized tooltip message for the current device status.
	 */
	protected readonly statusTooltip = computed(() => this.getTooltipMessage(this.deviceStatus()));
	/**
	 * Computed signal mapping the device status to a Prism component state string.
	 */
	protected readonly prismState = computed(() => this.mapToPrismState(this.deviceStatus()));

	constructor(private readonly translocoService: TranslocoService) {}

	/**
	 * Retrieves the localized tooltip message for the specified device status.
	 *
	 * @param status - The device status to get tooltip for
	 * @returns The translated tooltip message, or empty string for unknown status
	 */
	private getTooltipMessage(status: DeviceStatus): string {
		switch (status) {
			case DeviceStatus.Online:
				return this.translocoService.translate('scDeviceStateBar.online');
			case DeviceStatus.Offline:
				return this.translocoService.translate('scDeviceStateBar.offline');
			case DeviceStatus.Error:
				return this.translocoService.translate('scDeviceStateBar.device-discovery-error');
			default:
				return '';
		}
	}

	/**
	 * Maps a device status to the corresponding Prism status indicator state.
	 *
	 * @param status - The device status to map
	 * @returns The Prism state string ('good', 'bad', 'inactive', 'ok'), or empty string for unknown status
	 */
	private mapToPrismState(status: DeviceStatus): string {
		switch (status) {
			case DeviceStatus.Online:
				return 'good';
			case DeviceStatus.Offline:
				return 'inactive';
			case DeviceStatus.Discovered:
				return 'ok';
			case DeviceStatus.Error:
				return 'bad';
			default:
				return '';
		}
	}
}
